import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import './FAQ.css';

const FAQ = () => {
  const [openItem, setOpenItem] = React.useState(null);

  const toggleItem = (index) => {
    setOpenItem(openItem === index ? null : index);
  };

  const faqData = [
    {
      question: "What is Digital Fig?",
      answer: "Digital Fig is a creative and strategic collaboration; from seed to fruition - we build, grow, and support digital projects, products, and brands."
    },
    {
      question: "What kind of projects do you work on?",
      answer: "We work across digital media, e-commerce, design, content, and community-driven platforms. Every project is crafted with care and built to last."
    },
    {
      question: "What tools do you use to build sites?",
      answer: "Digital Fig employs top-tier software and platforms-ranging from Figma and Sketch for design, to WordPress, Webflow, or React-based frameworks for builds. We choose the best fit for your goals, performance needs, and budget."
    },
    {
      question: "Are Digital Fig websites SEO‑friendly?",
      answer: "Yes! Every site is built for performance and search success: Semantic HTML, clean code; Fast loading speeds; Mobile-first responsive design; Basic on-page SEO setup (meta tags, site speed optimization, sitemap)."
    },
    {
      question: "Who provides the content?",
      answer: "You're in full control. You own and supply the copy, images, content and media. Need help with ideas? Our expert copywriters and content strategists are available to help you finalise your project requirements."
    },
    {
      question: "Do I pay extra for hosting or SSL?",
      answer: "No extra charges! Hosting, SSL certificates, backups, and updates are included in your monthly bundle. No hidden costs!"
    },
    {
      question: "Do you outsource any work?",
      answer: "All core design and development is done in-house by our talented Digital Fig team. Occasionally, we engage trusted specialists for advanced integrations—always under our quality control."
    },
    {
      question: "How do you arrange client meetings?",
      answer: "Our clients are our priority. Meetings and client support are fully remote-friendly at a convenient time for you."
    },
    {
      question: "How are your prices so cheap?",
      answer: "We focus on what matters - great websites without the extras you don’t need."
    },
    {
      question: "If I already have a website, can I switch my maintenance to you?",
      answer: "Yes, certainly! We can even offer cheaper rates than your current provider."
    },
    {
      question: "So how do I get started?",
      answer: "Just contact us! Use our contact form, email, WhatsApp, or give us a call — we’ll take it from there."
    }
  ];

  const consultationOptions = [
    {
      title: 'Rapid Site Support',
      price: '£35',
      duration: '30 minutes',
      summary: 'Triage call covering minor fixes, hosting issues, or content updates with actionable next steps.',
      focusAreas: [
        'Screen-share audit of your site health',
        'Live fixes or clear to-do list',
        'Follow-up summary within 24 hours'
      ],
      ctaLabel: 'Book site support'
    },
    {
      title: 'Website Strategy Session',
      price: '£65',
      duration: '60 minutes',
      summary: 'Deep dive into UX, conversion, and roadmap planning for your next launch or redesign.',
      focusAreas: [
        'Review of goals, audience, and analytics',
        'Prioritised improvement plan',
        'Budget + timeline guidance'
      ],
      ctaLabel: 'Plan my website'
    },
    {
      title: 'AI Integration Discovery',
      price: '£55',
      duration: '45 minutes',
      summary: 'Identify the highest-impact automations and data touchpoints to connect with AI safely.',
      focusAreas: [
        'Process + tooling walkthrough',
        'Use-case scoring matrix',
        'Recommended stack + guardrails'
      ],
      ctaLabel: 'Scope AI ideas'
    },
    {
      title: 'AI Implementation Sprint',
      price: '£95',
      duration: '75 minutes',
      summary: 'Hands-on working session to prototype automations or co-build with your internal team.',
      focusAreas: [
        'Pair-program or workflow build',
        'Access + security checklist',
        'Next sprint backlog items'
      ],
      ctaLabel: 'Start the sprint'
    }
  ];

  const consultationRows = [];
  for (let i = 0; i < consultationOptions.length; i += 2) {
    consultationRows.push(consultationOptions.slice(i, i + 2));
  }

  return (
    <section id="faq" className="faq section">
      <div className="container">
        <motion.div 
          className="section-title"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2>Frequently Asked Questions</h2>
          <p>Everything you need to know about Digital Fig</p>
        </motion.div>
        
        <div className="faq-container">
          {faqData.map((item, index) => (
            <motion.div 
              key={index}
              className={`faq-item ${openItem === index ? 'open' : ''}`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <button 
                className="faq-question" 
                onClick={() => toggleItem(index)}
                aria-expanded={openItem === index}
              >
                <span>{item.question}</span>
                <FontAwesomeIcon 
                  icon={faChevronDown} 
                  className={`faq-icon ${openItem === index ? 'rotated' : ''}`}
                />
              </button>
              <div className={`faq-answer ${openItem === index ? 'expanded' : ''}`}>
                <p>{item.answer}</p>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="consultations-block">
        <motion.div 
          className="section-title"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2>No-Obligation Consultations</h2>
          <p>Get expert advice and guidance for your business needs.</p>
        </motion.div>
        
        <div className="consultation-grid">
          {consultationRows.map((row, rowIndex) => (
            <div className="consultation-row" key={`consultation-row-${rowIndex}`}>
              {row.map((option, index) => {
                const animationDelay = 0.1 * (rowIndex * 2 + index + 1);
                return (
                  <motion.div
                    key={option.title}
                    className="consultation-box"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: animationDelay }}
                  >
                    <div className="consultation-header">
                      <h4>{option.title}</h4>
                      <div className="consultation-meta">
                        <span className="consultation-price">{option.price}</span>
                        <span className="consultation-duration">{option.duration}</span>
                      </div>
                    </div>
                    <p className="consultation-summary">{option.summary}</p>
                    <ul className="consultation-focus">
                      {option.focusAreas.map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                    <Link
                      to="/contact"
                      className="consultation-cta"
                      aria-label={`${option.title} - ${option.ctaLabel}`}
                    >
                      {option.ctaLabel}
                    </Link>
                  </motion.div>
                );
              })}
            </div>
          ))}
        </div>
        </div>
      </div>
    </section>
  );
};

export default FAQ;
